/**
 * Tenants cell content for a single unit row. Lazy-loads the unit's tenants when the
 * row renders (i.e. when the property card is expanded) and shows a one-line skeleton
 * while the request is in flight.
 */
import { useCallback, useLayoutEffect, useState } from 'react';

import { Box, Gleam, Heading, Paragraph, Popover, TextButton, UnstyledButton } from '@zillow/constellation-10';

import { useFlexiblePropertiesSearch } from '../../../contexts/FlexiblePropertiesSearchContext';
import { usePropertyTableTenants } from '../../../hooks/queries/propertyTable';

import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import { truncatedTextStyles } from '../../../styles/propertyCardTable';
import { tenantsListStyles, tenantsOverflowGleamStyles, tenantsOverflowTriggerStyles } from '../styles';

import SkeletonCell from './SkeletonCell';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

// Show up to this many tenant names inline; the rest collapse into a "+N" popover.
const MAX_VISIBLE_TENANTS = 2;

type Props = {
  listingAlias: ListingAlias;
  isMultifamily?: boolean;
  onAddTenants?: () => void;
};

const TenantsCell = ({ listingAlias, isMultifamily, onAddTenants }: Props) => {
  const { includeArchived } = useFlexiblePropertiesSearch();
  const { data, isFetching } = usePropertyTableTenants(listingAlias);

  const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE_TENANTS);

  const tenants = data?.tenantNames ?? [];
  const tenantsKey = tenants.join(',');

  // Reset to max whenever the tenant list changes or the viewport resizes so
  // a previously-reduced count can recover when more space becomes available.
  useLayoutEffect(() => {
    setVisibleCount(MAX_VISIBLE_TENANTS);
  }, [tenantsKey]);

  useLayoutEffect(() => {
    const handleResize = () => setVisibleCount(MAX_VISIBLE_TENANTS);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTruncationChange = useCallback((isTruncated: boolean) => {
    if (isTruncated) {
      setVisibleCount((c) => Math.max(1, c - 1));
    }
  }, []);

  if (isFetching) {
    return <SkeletonCell loadingWidth="100px" />;
  }

  const visibleTenants = tenants.slice(0, visibleCount);
  const overflowTenants = tenants.slice(visibleCount);

  if (visibleTenants.length === 0) {
    if (isMultifamily === true || includeArchived) {
      return <Paragraph textStyle="body-sm">—</Paragraph>;
    }

    return (
      <TextButton
        textStyle="body-sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAddTenants?.();
        }}
      >
        Add tenants
      </TextButton>
    );
  }

  return (
    <Box css={tenantsListStyles}>
      <TruncatedTextWithTooltip
        text={visibleTenants.join(', ')}
        textStyle="body-sm"
        css={truncatedTextStyles}
        onTruncationChange={handleTruncationChange}
      />
      {overflowTenants.length > 0 && (
        <Popover
          tone="neutral"
          header={<Heading level={2}>Additional tenants</Heading>}
          body={
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
              {overflowTenants.map((name) => (
                <Paragraph key={name} textStyle="body-sm">
                  {name}
                </Paragraph>
              ))}
            </Box>
          }
          trigger={
            <UnstyledButton
              aria-label={`Show ${overflowTenants.length} more tenants`}
              css={tenantsOverflowTriggerStyles}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Gleam count={overflowTenants.length} css={tenantsOverflowGleamStyles} />
            </UnstyledButton>
          }
        />
      )}
    </Box>
  );
};

export default TenantsCell;
